import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { SalesWasteSummary } from '@/types/SalesSummary.interface';
import {
  ReportCard,
  ReportEmpty,
  ReportStatPair
} from '@/components/salesReport/ReportParts';
import {
  WASTE_TYPE_META,
  formatCount,
  formatMoney,
  formatPercent,
  formatReportDateTime,
  percentOf
} from '@/utils/salesReport';

interface WasteStatsProps {
  waste: SalesWasteSummary;
  timezone: string;
  onPageChange: (page: number) => void;
  pageLoading?: boolean;
}

const WASTE_COLOR = '#EA580C';

/**
 * Zayi / iç tüketim — satış raporunun `waste` bloğu (web POS ile aynı veri).
 * Bu kayıtlar 0 TL'lik siparişlerdir ve ciroya girmez; tutarlar liste fiyatı
 * maliyetidir. Ürün başına maliyet + sayfalı kayıt listesi (ürünler, personel,
 * not).
 */
const WasteStats = ({
  waste,
  timezone,
  onPageChange,
  pageLoading = false
}: WasteStatsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { page, totalPages, total } = waste.recordsPagination;

  return (
    <ReportCard
      title={t('dashboard.waste.stats')}
      icon={<DeleteOutlineIcon sx={{ color: WASTE_COLOR }} />}
      footnote={t('dashboard.waste.stats.hint')}
    >
      {waste.totalCount === 0 ? (
        <ReportEmpty />
      ) : (
        <>
          <ReportStatPair
            items={[
              {
                label: t('dashboard.waste.total.count'),
                value: formatCount(waste.totalCount)
              },
              {
                label: t('dashboard.waste.total.cost'),
                value: formatMoney(waste.totalCost),
                color: WASTE_COLOR
              }
            ]}
          />

          <Stack spacing={1.5}>
            {waste.byCategory.map((cat) => {
              const meta = WASTE_TYPE_META[cat.wasteType ?? ''];
              const label = meta ? t(meta.labelKey) : (cat.wasteType ?? '—');
              const color = meta?.color ?? theme.palette.grey[500];
              return (
                <Box key={cat.wasteType ?? 'none'}>
                  <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
                    <Typography variant="body1" sx={{ flex: 1 }}>
                      {label}{' '}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ textTransform: 'none' }}
                      >
                        ({formatCount(cat.count)})
                      </Typography>
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ textTransform: 'none' }}
                      color="text.secondary"
                    >
                      {formatPercent(cat.totalCost, waste.totalCost)}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color }}>
                      {formatMoney(cat.totalCost)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      background: alpha(color, 0.2)
                    }}
                  >
                    <Box
                      sx={{
                        width: `${percentOf(cat.totalCost, waste.totalCost)}%`,
                        height: '100%',
                        borderRadius: 1,
                        background: color
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            {t('salesReport.waste.products')}
          </Typography>
          {waste.topWastedProducts.map((product) => (
            <Box
              key={product.id}
              display="flex"
              alignItems="baseline"
              gap={1}
              py={0.5}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {product.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('salesReport.count', {
                  n: formatCount(product.quantity)
                })}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ minWidth: 110, textAlign: 'right' }}
              >
                {formatMoney(product.cost)}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />
          <Box display="flex" alignItems="center" mb={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {t('salesReport.waste.records')} ({formatCount(total)})
            </Typography>
          </Box>
          <Stack
            divider={<Divider flexItem />}
            spacing={1}
            sx={{ opacity: pageLoading ? 0.5 : 1 }}
          >
            {waste.records.map((record) => {
              const meta = WASTE_TYPE_META[record.wasteType ?? ''];
              return (
                <Box key={record.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatReportDateTime(record.createdAt, timezone)}
                    </Typography>
                    {meta && (
                      <Chip
                        size="small"
                        label={t(meta.labelKey)}
                        sx={{
                          height: 20,
                          color: meta.color,
                          backgroundColor: alpha(meta.color, 0.12)
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" fontWeight={700}>
                      {formatMoney(record.cost)}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {record.products
                      .map((p) => `${p.name} ×${formatCount(p.quantity)}`)
                      .join(', ')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ textTransform: 'none' }}
                    color="text.secondary"
                  >
                    {record.employeeName ?? '—'}
                    {record.branch ? ` · ${record.branch.name}` : ''}
                  </Typography>
                  {record.note && (
                    <Tooltip title={record.note}>
                      <Typography
                        variant="caption"
                        sx={{ textTransform: 'none' }}
                        color="text.secondary"
                        display="block"
                        noWrap
                      >
                        {t('salesReport.waste.note', { note: '' })}
                        {record.note}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Stack>
          {totalPages > 1 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              mt={1.5}
            >
              <Button
                size="small"
                startIcon={<ChevronLeft />}
                disabled={page <= 1 || pageLoading}
                onClick={() => onPageChange(page - 1)}
              >
                {t('salesReport.prev')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {t('salesReport.page', { page, total: totalPages })}
              </Typography>
              <Button
                size="small"
                endIcon={<ChevronRight />}
                disabled={page >= totalPages || pageLoading}
                onClick={() => onPageChange(page + 1)}
              >
                {t('salesReport.next')}
              </Button>
            </Box>
          )}
        </>
      )}
    </ReportCard>
  );
};

export default WasteStats;
